/* What this app is called, and who it belongs to.
 *
 * "Hird Ledger" was written into five screens, the manifest, the page title
 * and the sign-in card. None of it is wrong for Hird and all of it is wrong for
 * anybody else, which matters the moment this is deployed into a second tenant:
 * a recovery pack headed with the wrong company's name is not a cosmetic
 * problem, it is a document nobody trusts.
 *
 * Three settings, all optional, all build-time — they end up in the bundle and
 * in the manifest, neither of which can be decided at run time.
 *
 *   VITE_PRODUCT_NAME   what the app is called          default "BluePrint"
 *   VITE_ORG_NAME       whose it is                     default unset
 *   VITE_LOGO           a path under public/            default the BluePrint mark
 *
 * With no org set the app calls itself "BluePrint" everywhere and never claims
 * to belong to anybody. With one, it reads "Hird BluePrint".
 *
 * It defaulted to "Ledger" until 08/10/2026. Nothing was ever called that
 * outside the code: the repo, the SharePoint site and every list prefix say
 * Blueprint, and so does everybody who uses it. The page title said Hird
 * Ledger, which is the kind of mismatch that makes somebody wonder whether
 * they have opened the right thing.
 * Absent means unbranded rather than broken, which is the right default for a
 * tenant somebody has just stood up and not yet configured.
 */

const env = import.meta.env;

const name = (env.VITE_PRODUCT_NAME ?? 'BluePrint').trim() || 'BluePrint';
const org = (env.VITE_ORG_NAME ?? '').trim();

export const PRODUCT = {
  /** The product on its own — "Ledger". Tabs, badges, short labels. */
  name,

  /** The organisation, or '' when nobody has said. */
  org,

  /** How it signs itself — "Hird Ledger", or "Ledger" unbranded. */
  fullName: org ? `${org} ${name}` : name,

  /** For an asset label, where the point is who to ring rather than what the
   *  app is called — "Hird IT", or "IT" with no org. */
  itTeam: org ? `${org} IT` : 'IT',

  /** The mark in the header. A path under public/, so swapping it is dropping
   *  a file in and setting one variable rather than a rebuild of the shell.
   *  Defaults to the one that is already there. */
  /* The product's own mark, not the company's. It defaulted to Hird's logo
     until 08/10/2026, which meant an app with no identity of its own wearing
     somebody's company badge — and the wrong badge entirely in a second
     tenant. A tenant that wants its own still sets VITE_LOGO. */
  logo: (env.VITE_LOGO ?? '/brand/blueprint-mark.svg').trim() || '/brand/blueprint-mark.svg',

  /** The sign-in line. Says whose tenant to use, and cannot say it without an
   *  org name, so it drops the clause rather than saying "your  account". */
  signInHint: org
    ? `IT documentation and asset register. Sign in with your ${org} account to continue.`
    : 'IT documentation and asset register. Sign in to continue.',
} as const;
